import { useCallback, useEffect, useRef, useState } from "react";
import {
  CloseOutlined,
  CustomerServiceOutlined,
  DeleteOutlined,
  EditOutlined,
  FileOutlined,
  MinusOutlined,
  PaperClipOutlined,
  SearchOutlined,
  SendOutlined,
  UndoOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { Avatar, Badge, Button, Input, message, Tooltip } from "antd";
import { useSelector } from "react-redux";
import apiClient from "../../actions/api";
import { socket } from "../../untils/socket";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const isImage = (file) => file?.mimeType?.startsWith("image/");
const errorText = (error) =>
  error?.response?.data?.errors?.[0]?.msg || "Something went wrong.";

const SupportWindow = ({ user, currentUser, onClose, onActivity }) => {
  const [items, setItems] = useState([]);
  const [value, setValue] = useState("");
  const [reply, setReply] = useState(null);
  const [editing, setEditing] = useState(null);
  const [collapsed, setCollapsed] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [pendingAttachments, setPendingAttachments] = useState([]);
  const fileRef = useRef();
  const bottomRef = useRef();
  const userId = user._id;

  const load = useCallback(async () => {
    try {
      const { data } = await apiClient.get(`/support/messages/${userId}`);
      setItems(data);
      socket.emit("support_read", { userId });
      onActivity?.();
    } catch (error) {
      message.error(errorText(error));
    }
  }, [userId, onActivity]);

  useEffect(() => {
    load();
  }, [load]);
  useEffect(() => {
    if (!collapsed) bottomRef.current?.scrollIntoView();
  }, [items, collapsed]);
  useEffect(() => {
    const add = (item) => {
      if (String(item.userId) !== String(userId)) return;
      setItems((old) =>
        old.some((x) => x._id === item._id) ? old : [...old, item],
      );
      if (String(item.senderId) !== String(currentUser._id))
        socket.emit("support_read", { userId });
      onActivity?.();
    };
    const update = (item) =>
      String(item.userId) === String(userId) &&
      setItems((old) => old.map((x) => (x._id === item._id ? item : x)));
    socket.on("support_message", add);
    socket.on("support_message_updated", update);
    return () => {
      socket.off("support_message", add);
      socket.off("support_message_updated", update);
    };
  }, [currentUser._id, userId, onActivity]);

  const send = () => {
    if (uploading) return;
    const text = value.trim();
    if (!text && !pendingAttachments.length) return;
    if (editing)
      socket.emit("support_edit", { messageId: editing._id, message: text });
    else
      socket.emit("support_send", {
        userId,
        message: text,
        attachments: pendingAttachments,
        replyToId: reply?._id,
      });
    setValue("");
    setReply(null);
    setEditing(null);
    setPendingAttachments([]);
  };
  const chooseFiles = async (event) => {
    const files = [...event.target.files];
    event.target.value = "";
    if (!files.length) return;
    const form = new FormData();
    files.forEach((file) => form.append("files", file));
    setUploading(true);
    try {
      const { data } = await apiClient.post("/support/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPendingAttachments((current) =>
        [...current, ...data.attachments].slice(0, 5),
      );
    } catch (error) {
      message.error(errorText(error));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className={`support-window ${collapsed ? "is-collapsed" : ""}`}>
      <div
        className="support-window-head"
        onClick={() => collapsed && setCollapsed(false)}
      >
        <Avatar
          className="support-avatar"
          src={user.avatar}
          icon={<UserOutlined />}
        >
          {(user.name || "S").slice(0, 1).toUpperCase()}
        </Avatar>
        <div>
          <strong>{user.name || "Support"}</strong>
          <small>{user.email || "Support team"}</small>
        </div>
        <Button
          type="text"
          icon={<MinusOutlined />}
          aria-label="Collapse chat"
          onClick={(e) => {
            e.stopPropagation();
            setCollapsed((x) => !x);
          }}
        />
        <Button
          type="text"
          icon={<CloseOutlined />}
          aria-label="Close chat"
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
        />
      </div>
      {!collapsed && (
        <>
          <div className="support-messages">
            {!items.length && (
              <div className="support-empty">
                Start a conversation with {user.name || "the support team"}.
              </div>
            )}
            {items.map((item) => {
              const mine = String(item.senderId) === String(currentUser._id);
              return (
                <div
                  className={`support-message-row ${mine ? "mine" : "theirs"}`}
                  key={item._id}
                >
                  <div className="support-message-bubble">
                    {item.deletedAt ? (
                      <em className="support-deleted">Message deleted</em>
                    ) : (
                      <>
                        {item.replyTo && (
                          <div className="support-reply-preview">
                            {item.replyTo.message ||
                              item.replyTo.attachmentName}
                          </div>
                        )}
                        {item.attachments?.map((file, index) =>
                          isImage(file) ? (
                            <a
                              href={`${API_BASE_URL}${file.url}`}
                              target="_blank"
                              rel="noreferrer"
                              key={index}
                            >
                              <img
                                className="support-image"
                                src={`${API_BASE_URL}${file.url}`}
                                alt={file.name}
                              />
                            </a>
                          ) : (
                            <a
                              className="support-file"
                              href={`${API_BASE_URL}${file.url}`}
                              target="_blank"
                              rel="noreferrer"
                              key={index}
                            >
                              <FileOutlined /> {file.name}
                            </a>
                          ),
                        )}
                        {item.message && <div>{item.message}</div>}
                        <small>
                          {new Date(item.createdAt).toLocaleTimeString([], {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                          {item.editedAt ? " · edited" : ""}
                        </small>
                      </>
                    )}
                    {!item.deletedAt && (
                      <div className="support-message-actions">
                        <Tooltip title="Reply">
                          <Button
                            size="small"
                            type="text"
                            icon={<UndoOutlined />}
                            onClick={() => {
                              setReply(item);
                              setEditing(null);
                            }}
                          />
                        </Tooltip>
                        {mine && (
                          <>
                            <Tooltip title="Edit">
                              <Button
                                size="small"
                                type="text"
                                icon={<EditOutlined />}
                                onClick={() => {
                                  setEditing(item);
                                  setReply(null);
                                  setValue(item.message);
                                  setPendingAttachments([]);
                                }}
                              />
                            </Tooltip>
                            <Tooltip title="Delete">
                              <Button
                                size="small"
                                danger
                                type="text"
                                icon={<DeleteOutlined />}
                                onClick={() =>
                                  socket.emit("support_delete", {
                                    messageId: item._id,
                                  })
                                }
                              />
                            </Tooltip>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
            <div ref={bottomRef} />
          </div>
          {(reply || editing) && (
            <div className="support-compose-context">
              <span>
                {editing
                  ? "Editing message"
                  : `Replying to: ${reply.message || reply.attachments?.[0]?.name}`}
              </span>
              <CloseOutlined
                onClick={() => {
                  setReply(null);
                  setEditing(null);
                  setValue("");
                }}
              />
            </div>
          )}
          {pendingAttachments.length > 0 && (
            <div className="support-pending-files">
              {pendingAttachments.map((file, index) => (
                <div key={`${file.url}-${index}`}>
                  {isImage(file) ? (
                    <img src={`${API_BASE_URL}${file.url}`} alt={file.name} />
                  ) : (
                    <FileOutlined />
                  )}
                  <span title={file.name}>{file.name}</span>
                  <Button
                    type="text"
                    size="small"
                    icon={<CloseOutlined />}
                    aria-label={`Remove ${file.name}`}
                    onClick={() =>
                      setPendingAttachments((current) =>
                        current.filter((_, itemIndex) => itemIndex !== index),
                      )
                    }
                  />
                </div>
              ))}
            </div>
          )}
          <div className="support-compose">
            <input
              ref={fileRef}
              hidden
              multiple
              type="file"
              accept=".pdf,.docx,image/jpeg,image/png,image/gif,image/webp"
              onChange={chooseFiles}
            />
            <Button
              loading={uploading}
              shape="circle"
              icon={<PaperClipOutlined />}
              onClick={() => fileRef.current?.click()}
            />
            <Input.TextArea
              value={value}
              placeholder="Send a message"
              onChange={(e) => setValue(e.target.value)}
              autoSize={{ minRows: 1, maxRows: 5 }}
              onKeyDown={(event) => {
                if (event.key === "Enter" && !event.shiftKey) {
                  event.preventDefault();
                  send();
                }
              }}
            />
            <Button
              type="primary"
              shape="circle"
              icon={<SendOutlined />}
              disabled={
                uploading ||
                (!value.trim() && !pendingAttachments.length && !editing)
              }
              onClick={() => send()}
            />
          </div>
        </>
      )}
    </div>
  );
};

const SupportChat = ({ onOpenApplication, applicationOpen }) => {
  const currentUser = useSelector((state) => state.auth.user);
  const token = useSelector((state) => state.auth.token);
  const admin = Number(currentUser?.role) === 1;
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [conversations, setConversations] = useState([]);
  const [windows, setWindows] = useState([]);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);

  const refresh = useCallback(async () => {
    try {
      const [{ data: count }, list] = await Promise.all([
        apiClient.get("/support/unread"),
        admin
          ? apiClient.get("/support/conversations")
          : Promise.resolve({ data: [] }),
      ]);
      setUnread(count.count);
      if (admin) setConversations(list.data);
    } catch (_error) {}
  }, [admin]);

  useEffect(() => {
    const register = () => {
      socket.emit("register_user", currentUser);
      socket.emit("support_auth", token);
    };
    socket.on("connect", register);
    if (!socket.connected) socket.connect();
    else register();
    refresh();
    const activity = () => refresh();
    socket.on("support_message", activity);
    socket.on("support_read", activity);
    return () => {
      socket.off("connect", register);
      socket.off("support_message", activity);
      socket.off("support_read", activity);
    };
  }, [currentUser, refresh, token]);

  const openUser = (user) => {
    setWindows((old) =>
      old.some((x) => x._id === user._id) ? old : [...old, user],
    );
  };
  const search = async () => {
    if (!query.trim()) return setResults([]);
    try {
      const { data } = await apiClient.get("/support/search-users", {
        params: { q: query.trim() },
      });
      setResults(data);
    } catch (error) {
      message.error(errorText(error));
    }
  };
  const supportVisible = open || windows.length > 0;
  const closeAll = () => {
    setOpen(false);
    setWindows([]);
  };
  const toggle = () => {
    if (supportVisible) {
      closeAll();
      return;
    }
    setOpen(true);
    onOpenApplication?.();
  };

  return (
    <>
      <div className="support-window-tray">
        {windows.map((user) => (
          <SupportWindow
            key={user._id}
            user={user}
            currentUser={currentUser}
            onActivity={refresh}
            onClose={() =>
              setWindows((old) => old.filter((x) => x._id !== user._id))
            }
          />
        ))}
        {open && admin && (
          <div className="support-user-list">
            <div className="support-list-head">
              <CustomerServiceOutlined />
              <strong>Support conversations</strong>
              <Button type="text" icon={<CloseOutlined />} onClick={closeAll} />
            </div>
            <Input
              prefix={<SearchOutlined />}
              value={query}
              placeholder="Search name, email, or phone"
              onChange={(e) => setQuery(e.target.value)}
              onPressEnter={search}
            />
            <div className="support-users">
              {(results.length
                ? results.map((user) => ({ user }))
                : conversations
              ).map((row) => (
                <button
                  key={row.user._id}
                  className={
                    windows.some((item) => item._id === row.user._id)
                      ? "is-selected"
                      : ""
                  }
                  aria-pressed={windows.some(
                    (item) => item._id === row.user._id,
                  )}
                  onClick={() => openUser(row.user)}
                >
                  <Avatar
                    className="support-avatar"
                    src={row.user.avatar}
                    icon={<UserOutlined />}
                  >
                    {(row.user.name || "U").slice(0, 1).toUpperCase()}
                  </Avatar>
                  <span>
                    <strong>{row.user.name}</strong>
                    <small>
                      {row.user.email}
                      {row.lastMessage ? ` · ${row.lastMessage}` : ""}
                    </small>
                  </span>
                  {row.unread > 0 && <Badge count={row.unread} />}
                </button>
              ))}
              {!results.length && !conversations.length && (
                <div className="support-empty">
                  No support conversations yet. Search to message a user first.
                </div>
              )}
            </div>
          </div>
        )}
        {open && !admin && (
          <SupportWindow
            user={{
              _id: currentUser._id,
              name: "Support team",
              email: "Real-time help",
            }}
            currentUser={currentUser}
            onActivity={refresh}
            onClose={() => setOpen(false)}
          />
        )}
      </div>
      <Tooltip title="Support chat">
        <Badge count={unread} offset={[-4, 4]}>
          <Button
            className={`chat-launcher support-launcher ${supportVisible ? "active" : ""}`}
            onClick={toggle}
            type="primary"
            size="large"
            icon={
              supportVisible ? <CloseOutlined /> : <CustomerServiceOutlined />
            }
          />
        </Badge>
      </Tooltip>
    </>
  );
};

export default SupportChat;
